/**
 * Widget I: Employer Match Utilization
 * 
 * Displays a vertical bar chart showing the distribution of clients by
 * employer match utilization: No match plan, Under match, At/Above match.
 */

import React, { useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { ClientType } from "../../../types";
import { calculateEmployerMatchUtilization, EmployerMatchData } from "../utils/widgetCalculations";
import { UNIFIED_CHART_COLORS, SEMANTIC_PASTELS } from "./widgetColors";
import { ChartTooltip, BAR_CHART_MARGINS, AXIS_STYLE, GRID_STYLE, CHART_ANIMATION } from "./chartUtils";
import XanoAPI from "../../../lib/XanoAPI";
import { isDataEmpty, getSampleData } from "../utils/sampleData";
import { Badge } from "../../ui/badge";

interface EmployerMatchWidgetProps {
  clients: ClientType[];
  isLoading?: boolean;
  userId?: number; // Optional user ID filter (singular int)
}

const CATEGORY_LABELS = {
  noMatch: "No Match Plan",
  underMatch: "Under Match",
  atOrAboveMatch: "At/Above Match",
};

const CATEGORY_COLORS = {
  noMatch: UNIFIED_CHART_COLORS[9],      // Blue-gray (index 9)
  underMatch: SEMANTIC_PASTELS.warning,  // Lemon yellow (unified pastel)
  atOrAboveMatch: SEMANTIC_PASTELS.success, // Mint green (unified pastel)
};

export function EmployerMatchWidget({
  clients,
  isLoading = false,
  userId,
}: EmployerMatchWidgetProps) {
  const api = XanoAPI.getInstance();

  const { data: apiData, isLoading: apiLoading, error: apiError } = useQuery({
    queryKey: ["employer-match", userId],
    queryFn: async () => {
      const result = await api.getEmployerMatch(userId);
      console.log("🔍 EmployerMatchWidget: API response:", result);
      console.log("🔍 EmployerMatchWidget: API response type:", typeof result);
      console.log("🔍 EmployerMatchWidget: Has categories?", !!result?.categories);
      if (result?.categories) {
        console.log("🔍 EmployerMatchWidget: Categories:", JSON.stringify(result.categories, null, 2));
      }
      return result;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Log any errors
  useEffect(() => {
    if (apiError) {
      console.error("❌ EmployerMatchWidget: Query error:", apiError);
    }
  }, [apiError]);

  const data = useMemo(() => {
    let result: {
      categories: {
        noMatch: number;
        underMatch: number;
        atOrAboveMatch: number;
      };
    };
    
    console.log("🔍 EmployerMatchWidget: Processing data - apiData:", apiData);
    console.log("🔍 EmployerMatchWidget: apiData is null?", apiData === null);
    console.log("🔍 EmployerMatchWidget: apiData is undefined?", apiData === undefined);
    console.log("🔍 EmployerMatchWidget: Clients available:", clients.length);
    
    let usingSampleData = false;
    
    // Use API data if it exists and has categories structure with non-zero values
    if (apiData && apiData.categories && typeof apiData.categories === 'object' && Object.keys(apiData.categories).length > 0) {
      const totalClients = Object.values(apiData.categories).reduce((sum: number, val: any) => sum + (typeof val === 'number' ? val : 0), 0);
      console.log("🔍 EmployerMatchWidget: API data found - Total clients:", totalClients);
      console.log("🔍 EmployerMatchWidget: Categories structure:", JSON.stringify(apiData.categories, null, 2));
      
      if (totalClients > 0) {
        // API data has values - use it
        console.log("✅ EmployerMatchWidget: Using API data");
        result = apiData;
        usingSampleData = false;
      } else {
        // API data exists but is empty - try calculating from clients
        console.log("⚠️ EmployerMatchWidget: API data is empty (all zeros), trying client calculation");
        if (clients.length > 0) {
          try {
            const calculatedData = calculateEmployerMatchUtilization(clients);
            const calculatedTotal = Object.values(calculatedData.categories).reduce((a, b) => a + b, 0);
            console.log("🔍 EmployerMatchWidget: Calculated from clients - Total:", calculatedTotal);
            if (calculatedTotal > 0) {
              console.log("✅ EmployerMatchWidget: Using calculated data from clients");
              result = calculatedData;
              usingSampleData = false;
            } else {
              console.log("⚠️ EmployerMatchWidget: Calculated data also empty, using sample data");
              result = getSampleData.employerMatch();
              usingSampleData = true;
            }
          } catch (error) {
            console.error("❌ EmployerMatchWidget: Error calculating from clients:", error);
            result = getSampleData.employerMatch();
            usingSampleData = true;
          }
        } else {
          console.log("⚠️ EmployerMatchWidget: No clients available, using sample data");
          result = getSampleData.employerMatch();
          usingSampleData = true;
        }
      }
    } else {
      // No API data structure available - try calculating from clients first
      console.log("⚠️ EmployerMatchWidget: No API data structure available");
      console.log("🔍 EmployerMatchWidget: apiData:", apiData);
      
      if (clients.length > 0) {
        try {
          const calculatedData = calculateEmployerMatchUtilization(clients);
          const calculatedTotal = Object.values(calculatedData.categories).reduce((a, b) => a + b, 0);
          console.log("🔍 EmployerMatchWidget: Calculated from clients - Total:", calculatedTotal);
          if (calculatedTotal > 0) {
            console.log("✅ EmployerMatchWidget: Using calculated data from clients");
            result = calculatedData;
            usingSampleData = false;
          } else {
            console.log("⚠️ EmployerMatchWidget: Calculated data empty, using sample data");
            result = getSampleData.employerMatch();
            usingSampleData = true;
          }
        } catch (error) {
          console.error("❌ EmployerMatchWidget: Error calculating from clients:", error);
          result = getSampleData.employerMatch();
          usingSampleData = true;
        }
      } else {
        console.log("⚠️ EmployerMatchWidget: No clients available, using sample data");
        result = getSampleData.employerMatch();
        usingSampleData = true;
      }
    }
    
    // Transform to chart data format
    const totalClients = Object.values(result.categories).reduce((a: number, b: number) => a + b, 0);
    console.log("🔍 EmployerMatchWidget: Total clients:", totalClients);
    console.log("🔍 EmployerMatchWidget: Category entries:", Object.entries(result.categories));
    
    const chartData = Object.entries(result.categories)
      .filter(([_, count]) => count > 0)
      .map(([key, count]) => ({
        category: CATEGORY_LABELS[key as keyof typeof CATEGORY_LABELS],
        count,
        percentage: totalClients > 0 ? (count / totalClients) * 100 : 0,
        fill: CATEGORY_COLORS[key as keyof typeof CATEGORY_COLORS],
        key,
      }));
    
    console.log("🔍 EmployerMatchWidget: Chart data after filtering:", chartData);
    console.log("🔍 EmployerMatchWidget: Chart data length:", chartData.length);

    return { chartData, totalClients, usingSampleData };
  }, [apiData, clients]);

  const isLoadingData = isLoading || apiLoading;

  if (isLoadingData) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.totalClients === 0 || data.chartData.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground">
              No employer match data available for the selected clients.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartDataWithTotal = data.chartData.map(item => ({
    ...item,
    totalClients: data.totalClients,
  }));

  return (
    <Card>
      <CardContent>
        {data.usingSampleData && (
          <div className="mb-2 flex justify-end">
            <Badge variant="secondary" className="text-xs">Sample Data</Badge>
          </div>
        )}
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartDataWithTotal} margin={BAR_CHART_MARGINS}>
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis 
                dataKey="category" 
                angle={-45}
                textAnchor="end"
                height={70}
                interval={0}
                tick={AXIS_STYLE.tick}
                tickLine={false}
              />
              <YAxis 
                label={{ 
                  value: "Number of Clients", 
                  angle: -90, 
                  position: "insideLeft",
                  style: AXIS_STYLE.label
                }}
                allowDecimals={false}
                tick={AXIS_STYLE.tick}
                tickLine={false}
                width={25}
              />
              <Tooltip 
                content={<ChartTooltip 
                  formatter={(value, name) => {
                    const dataPoint = chartDataWithTotal.find(d => d.count === value);
                    return [
                      `${value} clients (${dataPoint?.percentage.toFixed(1)}%)`,
                      dataPoint?.category || name
                    ];
                  }}
                />} 
                cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
              />
              <Bar 
                dataKey="count" 
                radius={[8, 8, 0, 0]}
                {...CHART_ANIMATION}
              >
                {chartDataWithTotal.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 pt-3 border-t text-center">
          {data.chartData.map((item) => (
            <div key={item.key} className="space-y-1">
              <div className="text-lg font-bold" style={{ color: item.fill }}>
                {item.count}
              </div>
              <div className="text-xs font-medium text-foreground">{item.category}</div>
              <div className="text-xs text-muted-foreground">
                {item.percentage.toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

